import React, { useId, useState } from 'react';
import { FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';

/**
 * Labelled input with a leading icon, inline error, and — for password inputs —
 * a show/hide toggle.
 *
 * Errors are wired up with `aria-describedby` and `aria-invalid` so screen
 * readers announce the reason, not just that something is wrong.
 */
export default function TextField({
  label,
  name,
  type = 'text',
  icon: Icon,
  error,
  hint,
  value,
  onChange,
  onBlur,
  placeholder,
  autoComplete,
  required = false,
  children,
}) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && revealed ? 'text' : type;

  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mm-field">
      <label className="mm-field__label" htmlFor={id}>
        {label}
        {!required && <span className="mm-faint fw-normal"> (optional)</span>}
      </label>

      <div className="mm-field__wrap">
        {Icon && (
          <span className="mm-field__icon" aria-hidden="true">
            <Icon size={16} />
          </span>
        )}

        <input
          id={id}
          name={name}
          type={inputType}
          className={[
            'mm-field__control',
            isPassword ? 'mm-field__control--has-action' : '',
            error ? 'mm-field__control--invalid' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy || undefined}
          required={required}
        />

        {isPassword && (
          <button
            type="button"
            className="mm-field__action"
            onClick={() => setRevealed((prev) => !prev)}
            // The label changes with state so it always describes the action,
            // and `aria-pressed` conveys whether the password is visible.
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            tabIndex={-1}
          >
            {revealed ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        )}
      </div>

      {children}

      {error && (
        <span className="mm-field__error" id={errorId} role="alert">
          <FiAlertCircle size={13} aria-hidden="true" />
          {error}
        </span>
      )}

      {hint && !error && (
        <span className="mm-field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}
